import type { Address, CreateAddressRequest } from '@frontend/api-types';
import { useCallback, useEffect, useState } from '@frontend/react';
import { Button, Text, TextInput, YStack } from '@frontend/web-react';
import { concatClasses, safeCss } from '@frontend/web-utils';
import { useAddressAutocomplete } from '@frontend/web/hooks';

import { PROFILE_WIZARD_STEP_CONFIGS } from '../ProfileWizardCard.constants';
import { PROFILE_WIZARD_STEP } from '../ProfileWizardCard.types';

const STEP_CONFIG = PROFILE_WIZARD_STEP_CONFIGS.find((c) => c.step === PROFILE_WIZARD_STEP.address)!;
const BLUR_DELAY_FOR_CLICK_MS = 200;

type AddressStepProps = {
  existingAddress: MaybeUndefined<Address>;
  isSubmitting: boolean;
  onSubmit: (address: CreateAddressRequest) => void;
};

export const AddressStep = ({ existingAddress, isSubmitting, onSubmit }: AddressStepProps) => {
  const { clearSearch, locationResults, searchDetails, setTerm } = useAddressAutocomplete();
  const [displayValue, setDisplayValue] = useState(() =>
    existingAddress ? formatAddressDisplay(existingAddress) : '',
  );
  const [resolvedAddress, setResolvedAddress] = useState<Omit<Address, 'alias'> | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (existingAddress && !displayValue && !resolvedAddress) {
      setDisplayValue(formatAddressDisplay(existingAddress));
    }
  }, [existingAddress, displayValue, resolvedAddress]);

  const isValid = Boolean(resolvedAddress) || Boolean(existingAddress);

  const handleInputChange = useCallback(
    (value: string) => {
      setDisplayValue(value);
      setTerm(value);
      setResolvedAddress(null);
      setShowDropdown(true);
    },
    [setTerm],
  );

  const handleSelectResult = useCallback(
    async (placeId: string, text: string) => {
      setDisplayValue(text);
      setShowDropdown(false);
      clearSearch();
      const details = await searchDetails(placeId);
      if (details) {
        setResolvedAddress(details);
      }
    },
    [clearSearch, searchDetails],
  );

  const handleSubmit = useCallback(() => {
    if (resolvedAddress) {
      onSubmit({ alias: 'Home', ...resolvedAddress });
      return;
    }
    if (existingAddress) {
      onSubmit(existingAddress);
    }
  }, [existingAddress, onSubmit, resolvedAddress]);

  return (
    <YStack gapY="md">
      <YStack gapY="xs">
        <Text type="h4">{STEP_CONFIG.title}</Text>
        <Text color="secondary" type="p-sm">
          {STEP_CONFIG.description}
        </Text>
      </YStack>
      <div className="relative">
        <TextInput
          label=""
          onBlur={() => setTimeout(() => setShowDropdown(false), BLUR_DELAY_FOR_CLICK_MS)}
          onChange={handleInputChange}
          onFocus={() => {
            if (locationResults.length > 0) {
              setShowDropdown(true);
            }
          }}
          placeholder="123 Main St, City, State"
          value={displayValue}
        />
        {showDropdown && locationResults.length > 0 ? (
          /* eslint-disable-next-line tailwindcss/no-arbitrary-value */
          <div
            className={concatClasses(
              safeCss('absolute left-none right-none top-full z-50 mt-xs'),
              safeCss('max-h-[200px] overflow-y-auto rounded-sm border border-borderPrimary bg-trueWhite'),
            )}
            style={{ boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)' }}
          >
            {locationResults.map((result) => (
              <button
                className={safeCss('w-full cursor-pointer px-md py-sm text-left text-p-sm hover:bg-neutral-50')}
                key={result.placeId}
                onClick={() => handleSelectResult(result.placeId, result.text)}
                type="button"
              >
                {result.text}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <Button
        disabled={!isValid}
        fullWidth
        isLoading={isSubmitting}
        label={isValid ? 'Update' : 'Continue'}
        onClick={handleSubmit}
        size="lg"
        variant="primary"
      />
    </YStack>
  );
};

const formatAddressDisplay = (address: Address): string =>
  `${address.line1}${address.line2 ? ` ${address.line2}` : ''}, ${address.city}, ${address.stateCode} ${address.postalCode}`;
